// Оркестратор Engine A — Quote Selection (spec §2, §10).
//
// Конвейер: pre-filter → LLM-гейт рубрики (judge, инъектируется) → verbatim →
// dominance → per-stage select → trim → §VII-деривации → fallbacks.
//
// LLM-судья инъектируется как зависимость `judge` → ядро тестируется на моке,
// без реального вызова. В проде judge приходит из ./llm.js (§4, ждёт EN-канон
// рубрики + few-shot от web-Claude — спека §12).

import { normalizeStages, eligibleFields } from './input.js';
import { verbatimSlice } from './verbatim.js';
import { selectAcrossArc } from './selection.js';
import { trimDisplay } from './trim.js';
import { splitLines, lastSentence, tieredExtraction } from './sectionVII.js';
import { STACK_MAX, QUOTE_N_MAX, FIELDS } from './thresholds.js';
import {
  SILENT_QUOTE_FALLBACK,
  SLOT_A_FALLBACK,
  SLOT_C_FALLBACK,
  SLOT_F_FALLBACK,
} from './fallbacks.js';

/**
 * judge(fields, { isCarrier }) → { fields: [{ field, qualifies, phrase }] }
 *   fields  — { T1,T2,T3 } (только eligible заполнены, прочие '')
 *   возвращает суждение рубрики (§3) + вырезку phrase по каждому полю.
 *
 * @typedef {(fields: object, ctx: {isCarrier: boolean}) => {fields: Array}} Judge
 */

// Причина отсутствия цитаты на этапе (тикет «Различать причины отсутствия
// цитаты»). Значения per-stage НЕ называем именем инфраструктурного сбоя —
// иначе суммирование по этапам даст фальшивый счётчик сбоев (ревью web-Claude):
//   'empty'            — pre-filter отсёк, LLM не звали (факт о вводе человека);
//   'llm_declined'     — judge посмотрел, но не нашёл что процитировать (факт о тексте);
//   'verbatim_rejected'— модель выдала фразу, не прошедшую verbatim-проверку (факт
//                        о МОДЕЛИ, наш брак — отделено от llm_declined осознанно);
//   'not_evaluated'    — ЭТОТ этап не получил ответа (вызов упал) — честно «не
//                        знаем, была ли годная фраза», а НЕ «человек не написал».
// Частичная деградация (D&R переписан под per-stage транспорт): упавший этап
// помечается not_evaluated ПО ОТДЕЛЬНОСТИ, уцелевшие держат цитаты — весь документ
// не роняем. out.transport — рун-уровень «как прошёл прогон» (reason
// 'transport_failed'). d1: not_evaluated считается по этапам, out.transport
// отвечает на другой вопрос; два счёта НЕ складывать. Имени 'transport_failed' на
// этапах нет — суммой по этапам счётчик сбоя не подделать.

/**
 * Лог по умолчанию: пишем всегда (решение #3). ТОЛЬКО коды/числа/статус —
 * без текста полей, тела ответа модели и промпта (s311.d6, GDPR). Формат
 * провизорный — финал в тикете метрик.
 */
// eslint-disable-next-line no-console
const defaultLog = (ev) => console.warn('[quotes]', ev);

/**
 * @param {Array} rawStages         [{ T1,T2,T3 }] (см. input.js)
 * @param {object} opts
 * @param {number[]} opts.carrierStages  индексы carrier-этапов (System 1)
 * @param {Judge}    opts.judge          LLM-судья рубрики (обязателен)
 * @param {(ev:object)=>void} [opts.log] сток событий (дефолт — console.warn)
 */
// Асинхронный: judge — это LLM-вызов (§4). Детерминированное ядро (dominance /
// select / trim / §VII) синхронно; await только на гейте рубрики.
export async function selectQuotes(rawStages, { carrierStages = [], judge, log = defaultLog } = {}) {
  if (typeof judge !== 'function') {
    throw new Error(
      'selectQuotes: `judge` required (LLM rubric gate). См. ./llm.js / spec §4.',
    );
  }
  const { stages, n } = normalizeStages(rawStages);
  const carrierSet = new Set(carrierStages);

  // 1–3. qualifying-поля per этап (рубрика + verbatim). Per-stage изолировано (§4.2).
  // Сбой judge ловим ПОСТАДИЙНО, чтобы одно исключение не роняло весь Promise.all
  // (и весь документ) — деградируем осознанно ниже.
  const perStageRaw = await Promise.all(
    stages.map(async (stage, i) => {
      const eligible = eligibleFields(stage);
      if (Object.keys(eligible).length === 0) return { q: {}, called: false }; // пустой этап — LLM не зовём

      const payload = Object.fromEntries(
        FIELDS.map((F) => [F, eligible[F] ?? '']),
      );

      let verdict;
      try {
        verdict = (await judge(payload, { isCarrier: carrierSet.has(i) })) || {};
      } catch (err) {
        return { q: {}, called: true, transportError: err };
      }

      const q = {};
      let verbatimRejected = false;
      for (const fv of verdict.fields || []) {
        if (!fv || !fv.qualifies) continue;
        if (eligible[fv.field] == null) continue; // judge не вправе квалифицировать pre-filtered поле
        const slice = verbatimSlice(fv.phrase, stage[fv.field]);
        if (slice != null) q[fv.field] = slice; // survivor
        else verbatimRejected = true; // модель выдала непроверяемую фразу — брак модели
      }
      return { q, called: true, verbatimRejected };
    }),
  );

  // ЧАСТИЧНАЯ ДЕГРАДАЦИЯ (D&R переписан под per-stage транспорт, вариант 2):
  // вызов per-stage (свой на этап), поэтому упавший этап НЕ роняет остальные —
  // уцелевшие держат цитаты, упавший помечается not_evaluated. Схлопывания всего
  // документа больше нет.
  const failedCount = perStageRaw.filter((r) => r.transportError).length;
  const firstError = perStageRaw.find((r) => r.transportError)?.transportError || null;

  // out.transport — РУН-уровень: «как прошёл прогон целиком», НЕ per-stage счётчик.
  // Число затронутых этапов = число per-stage not_evaluated; д1: два счёта НЕ
  // складывать (метрики читают либо not_evaluated по этапам, либо out.transport
  // как факт прогона — не оба).
  let transport;
  if (failedCount > 0) {
    transport = {
      ok: false,
      reason: 'transport_failed',
      attempts: firstError.attempts ?? null,
      status: firstError.status ?? null,
    };
    log({ event: 'quotes_transport_failed', ...transport });
  } else {
    transport = { ok: true };
  }

  // 4–5. dominance + per-stage select. Упавшие этапы дают пустой q → цитаты нет
  // ТОЛЬКО там; dominance считается по уцелевшим (упавший = как пустой этап).
  const qualified = perStageRaw.map((r) => r.q);
  const selected = selectAcrossArc(qualified);

  // выходы
  const current = n >= 1 ? selected[n - 1] : null;
  const penult = n >= 2 ? selected[n - 2] : null;

  const sectionVI = {
    // A2 stack = этапы 0..N−2; A3 quote_n = current (N−1)
    quoteStack: selected.slice(0, Math.max(0, n - 1)).map((sel, i) => ({
      stage: i,
      text: sel ? trimDisplay(sel.quote, STACK_MAX) : SILENT_QUOTE_FALLBACK,
      silent: !sel,
    })),
    quoteN: current ? trimDisplay(current.quote, QUOTE_N_MAX) : SILENT_QUOTE_FALLBACK,
    quoteNSilent: !current,
    // footer count = этапы с РЕАЛЬНОЙ цитатой (§9)
    footerCount: selected.filter(Boolean).length,
  };

  const sectionVII = {
    slotA: current ? splitLines(current.quote) : [SLOT_A_FALLBACK], // A4
    slotF: current ? lastSentence(current.quote) : SLOT_F_FALLBACK, // A6
    slotC: penult ? tieredExtraction(penult.quote) : SLOT_C_FALLBACK, // A5
  };

  const sectionIV = {
    // A1 + A7 (mirror): по carrier-этапам, по возрастанию индекса
    evidence: [...carrierSet]
      .filter((i) => i >= 0 && i < n)
      .sort((a, b) => a - b)
      .map((i) => ({ stage: i, quote: selected[i] ? selected[i].quote : null })),
  };

  return {
    perStage: selected.map((sel, i) => {
      let reason = null;
      if (!sel) {
        const raw = perStageRaw[i];
        if (raw.transportError) reason = 'not_evaluated'; // ЭТОТ этап не получил ответа (per-stage, не весь документ)
        else if (!raw.called) reason = 'empty'; // pre-filter — факт о вводе
        else if (raw.verbatimRejected) reason = 'verbatim_rejected'; // модель дала брак — факт о модели
        else reason = 'llm_declined'; // judge отработал, квалифицировать нечего — факт о тексте
      }
      return {
        stage: i,
        isCarrier: carrierSet.has(i),
        field: sel ? sel.field : null,
        quote: sel ? sel.quote : null,
        reason,
        no_quote: !sel, // = reason !== null; «цитаты нет по любой причине», не «человек не написал»
      };
    }),
    transport, // документ-уровень: единый факт транспорт-сбоя (метрики читают отсюда)
    sectionVI,
    sectionVII,
    sectionIV,
  };
}
